import { PLAN_LEVEL, PLAN_ORDER, PLAN_TYPES } from "./plans";
import { createSubscriptionFeatures } from "./subscriptionFeatures";

export function isPlanLocked(userPlan, requiredPlan) {
  const userLevel = PLAN_LEVEL[userPlan] ?? 0;
  const productLevel = PLAN_LEVEL[requiredPlan] ?? 0;

  if (!userPlan && productLevel >= 1) return true;

  return productLevel > userLevel;
}

export const getFeatureStatus = (isEnabled) => isEnabled ? "active" : "locked";

export const buildSubscriptionCards = (data, selectedPeriod, t, lang) => {
  const sortedPlans = [...data].sort(
    (a, b) => PLAN_ORDER.indexOf(a.plan) - PLAN_ORDER.indexOf(b.plan)
  );

  return sortedPlans.map((plan) => {
    const selectedPrice = plan.periods.find((p) => p.period === selectedPeriod);

    return {
      id: plan._id,
      plan: plan.plan,
      price: selectedPrice?.price || 0,
      currency: selectedPrice?.currency || "USD",
      period: selectedPeriod,
      features: createSubscriptionFeatures({ entitlements: plan.entitlements, plan: plan.plan, t, lang }),
      isPopular: plan.plan === PLAN_TYPES.FAMILY,
    };
  });
};

export const calculateYearlySavings = (subscriptions) => {
  const savings = {};

  subscriptions.forEach((plan) => {
    const monthly = plan.periods.find((p) => p.period === "monthly");
    const yearly = plan.periods.find((p) => p.period === "yearly");

    if (monthly && yearly) {
      const monthlyTotal = monthly.price * 12;
      const percentSaved = ((monthlyTotal - yearly.price) / monthlyTotal) * 100;
      savings[plan.plan] = Math.round(percentSaved);
    }
  });

  const maxSaving = Math.max(...Object.values(savings || {}));
  return maxSaving;
};

export const getPlanPrices = (plans, planKey) => {
  const plan = plans.find((p) => p.plan === planKey?.toUpperCase());
  if (!plan || !plan.periods) {
    return { monthly: 0, yearly: 0, amdMonthly: 0, amdYearly: 0 };
  }
  const monthly = plan.periods.find((p) => p.period === "monthly")
  const yearly = plan.periods.find((p) => p.period === "yearly")

  return {
    monthly: monthly?.price || 0,
    yearly: yearly?.price || 0,
    amdMonthly: monthly?.amdPrice || 0,
    amdYearly: yearly?.amdPrice || 0,
  };
};


export const buildSubscriptionRows = (sortedPlans, t) => {
  if (!sortedPlans?.length) return [];

  const getEntitlement = (plan, type) => plan.entitlements?.find((e) => e?.type === type);

  return [
    {
      feature: t("books"),
      values: sortedPlans.map((plan) => {
        const e = getEntitlement(plan, "books");
        return e?.access === "FULL" ? t("all") : e?.limit > 1 ? String(e?.limit) : "✘"
      }),
    },
    {
      feature: t("cartoons"),
      values: sortedPlans.map((plan) => {
        const e = getEntitlement(plan, "cartoons");
        return e?.access === "FULL" ? t("all") : e?.limit > 1 ? String(e?.limit) : "✘"
      }),
    },
    {
      feature: t("puzzles"),
      values: sortedPlans.map((plan) => {
        const e = getEntitlement(plan, "puzzles");
        return e?.access === "FULL" ? t("all") : e?.limit > 1 ? String(e?.limit) : "✘"
      }),
    },
    {
      feature: t("painting"),
      values: sortedPlans.map((plan) => {
        const e = getEntitlement(plan, "paintings");
        return e?.access === "FULL" ? t("all") : e?.limit > 1 ? String(e?.limit) : "✘"
      }),
    },
    {
      feature: t("bedtime_songs"),
      values: sortedPlans.map((plan) => {
        const e = getEntitlement(plan, "bedtime");
        return e?.access === "FULL" ? t("all") : e?.limit > 1 ? String(e?.limit) : "✘"
      }),
    },
    {
      feature: t("invite_family"),
      values: sortedPlans.map((plan) => {
        const e = getEntitlement(plan, "familySharing");
        return e?.limit > 1 ? "✓" : "✘"
      }),
    },
    {
      feature: t("offline_use_in_the_mobile_app"),
      values: sortedPlans.map((plan) => {
        const e = getEntitlement(plan, "familySharing");
        return e?.access === "FULL" ? "✓" : "✘"
      }),
    },
    {
      feature: t("ai_assistant"),
      values: sortedPlans.map((plan) => {
        const e = getEntitlement(plan, "aiAssistant");

        if (!e) return "-";

        if (plan.plan === PLAN_TYPES.FAMILY) return t("unlimited");
        if (plan.plan === PLAN_TYPES.PREMIUM) return `${e.limit} ${t("request")} / ${e.resetPeriodHours} ${t("hours")}`;
        if (plan.plan === PLAN_TYPES.FREE) return e?.limit ? `${String(e.limit)} ${t("request")}` : "✘";

        return t("not_available");
      }),
    },
  ];
};

export function hasFeatureAccess(plans, userPlan, featureKey) {
  const plan = plans.find(p => p.plan === userPlan);
  const feature = plan?.entitlements?.find(item => item.type === featureKey);

  if (!feature) return false;

  return ["FULL", "ALL", "LIMITED"].includes(feature?.access) || feature?.limit > 0 || feature?.freeUsageLimit > 0;
}

export function getRequiredPlan(plans, featureKey) {
  return PLAN_ORDER.find(planName => {
    const plan = plans.find(p => p.plan === planName);
    const feature = plan?.entitlements?.find(e => e.type === featureKey); // ← fix
    
    if (!feature) return false;
    
    return ["FULL", "LIMITED"].includes(feature.access);
  });
}

export function isDowngrade(currentPlan, targetPlan) {
  const currentIndex = PLAN_ORDER.indexOf(currentPlan);
  const targetIndex = PLAN_ORDER.indexOf(targetPlan);
  return targetIndex < currentIndex;
}
